import { cn, RiArrowLeftLine, Stack, StackProps } from 'component-library';
import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { ButtonWidget } from '@/components/widgets/a/Button/Button';
import { ButtonLinkWidget } from '@/components/widgets/a/Button/ButtonLink';
import { ProgressProps, ProgressWidget } from '@/components/widgets/a/Progress/Progress';

import { CoPilotLogo } from '../CoPilot/components/CoPilot';

type SplitViewProps = {
  children: ReactNode;
  className?: string;
};

export const SplitView = ({ children, className }: SplitViewProps) => {
  return <div className={cn('grid grid-cols-2 gap-4 w-screen h-screen', className)}>{children}</div>;
};

type GuidedContainerProps = {
  children: ReactNode;
  className?: string;
  progressProps?: ProgressProps;
  footer?: GuidedContainerFooterProps;
} & StackProps;

export const GuidedContainer = ({
  children,
  className,
  direction = 'col',
  spacing = 6,
  progressProps,
  footer,
  ...stackProps
}: GuidedContainerProps) => {
  return (
    <BaseContainer className={className} direction={direction} spacing={spacing} {...stackProps}>
      <CoPilotLogo data-testid='co-pilot-logo' className='p-0' />
      {progressProps && <ProgressWidget data-testid='progress-widget' {...progressProps} />}
      <section className='grow overflow-y-auto'>{children}</section>
      <GuidedContainerFooter {...footer} />
    </BaseContainer>
  );
};

export type GuidedContainerFooterProps = {
  nextActionType?: 'button' | 'submit' | 'reset';
  disableNextAction?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nextAction?: (args: any) => void;
  backPath?: string;
  showFooter?: boolean;
  nextText?: string;
  nextVariant?: 'contained' | 'outline' | 'text';
  disableBackAction?: boolean;
};

export const GuidedContainerFooter = ({
  nextAction,
  nextActionType = 'button',
  backPath,
  disableNextAction = false,
  disableBackAction = false,
  showFooter = true,
  nextText = 'global.journey.buttons.next',
  nextVariant = 'contained',
}: GuidedContainerFooterProps) => {
  const { state } = useLocation();

  if (!showFooter) return null;

  return (
    <Stack direction='row' className='justify-between w-full'>
      <ButtonLinkWidget
        to={backPath ?? state?.from ?? ''}
        variant='outline'
        size='large'
        className='p-3'
        disabled={disableBackAction}
      >
        <RiArrowLeftLine size={24} />
      </ButtonLinkWidget>
      <ButtonWidget
        onClick={nextAction}
        size='large'
        className='py-3 px-12'
        i18nPath={nextText}
        type={nextActionType}
        disabled={disableNextAction}
        variant={nextVariant}
      />
    </Stack>
  );
};

type BaseContainerProps = {
  children: ReactNode;
  showFooter?: boolean;
  className?: string;
  progressProps?: ProgressProps;
  nextAction?: () => void;
} & StackProps;

export const BaseContainer = ({ children, className, direction, spacing, ...stackProps }: BaseContainerProps) => {
  return (
    <Stack className={cn('h-full w-full p-6', className)} direction={direction} spacing={spacing} {...stackProps}>
      {children}
    </Stack>
  );
};
